import { InfoWindowF, MarkerF } from "@react-google-maps/api";
// Local
import { ExistingSiteKeyLocation } from "../../../models/site-key-location";
import { ExistingSiteKeyUserPermissions } from "../../../models/site-key-user-permissions";
import { ExistingTask } from "../../../models/task";
import { ResourceTechnicianVehicle } from "../../../Pages/Scheduling/SchedulingDetailContainer";
import { WORK_RECORD_AND_TASKS_URL } from "../../../urls";
import { getOriginForResource } from "./getDirections";

/** get map markers based on the given resources.
 * returns markers for stops (where a task is located/job is performed) and for
 * the start/end (where a vehicle leaves/returns) points. */
export default function getMapMarkers(args: {
  resources: ResourceTechnicianVehicle[];
  siteKeyLocations: ExistingSiteKeyLocation[];
  permissionsMap: Record<string, ExistingSiteKeyUserPermissions>;
  hoverTaskID: string | null;
  selectedMarker: ExistingTask | null;
  setSelectedMarker: (task: ExistingTask | null) => void;
  mapCenter: { lat: number; lng: number };
}): { stopMarkers: React.ReactNode[]; startEndMarkers: React.ReactNode[] } {
  // Get all tasks in order across all resources
  const orderedTasks: (ExistingTask & { vehicleName: string | null })[] = [];
  for (const r of args.resources) {
    const name = r.vehicle ? r.vehicle.title : null;
    orderedTasks.push(
      ...getOrderedTasks(r).map((t) => ({ ...t, vehicleName: name })),
    );
  }

  const stopMarkers = orderedTasks.map((task, index) => {
    return (
      <span key={task.id + "-" + index.toString()}>
        <MarkerF
          onClick={() => args.setSelectedMarker(task)}
          position={{
            lat: task.latitude!,
            lng: task.longitude!,
          }}
          // TODO: marker collision management. need to migrate to new marker api tho
          // https://developers.google.com/maps/documentation/javascript/advanced-markers/migration
          label={{
            text: orderedTasks.length > 99 ? " " : (index + 1).toString(),
            color: "black",
            fontWeight: args.hoverTaskID === task.id ? "bold" : "normal",
            fontSize: "14px",
          }}
          icon={{
            labelOrigin:
              typeof window !== "undefined" && window.google?.maps
                ? new window.google.maps.Point(15, 10)
                : undefined,
            url:
              args.hoverTaskID === task.id
                ? "https://maps.google.com/mapfiles/ms/icons/blue.png"
                : "https://maps.google.com/mapfiles/ms/icons/red.png",
          }}
        />
        {args.selectedMarker?.id === task.id && (
          <InfoWindowF
            position={{
              lat: task.latitude!,
              lng: task.longitude!,
            }}
            onCloseClick={() => args.setSelectedMarker(null)}
          >
            <div className="px-2 pb-2">
              {task.vehicleName && <p className="mb-1">{task.vehicleName}</p>}
              <h3 className="font-bold">{task.title}</h3>
              <p className="mt-1 text-sm text-gray-600">{task.description}</p>
              <button
                className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                onClick={() => {
                  const craftRecordID = task.craftRecordID.split("/")[3];
                  const url = `${WORK_RECORD_AND_TASKS_URL}/${craftRecordID}`;
                  window.open(url, "_blank");
                }}
              >
                View Details
              </button>
            </div>
          </InfoWindowF>
        )}
      </span>
    );
  });

  const startEndMarkers = args.resources
    .filter((r) => r.taskList.length > 0)
    .map((resource, index) => {
      const origin = getOriginForResource({
        resource,
        siteKeyLocations: args.siteKeyLocations,
        permissionsMap: args.permissionsMap,
        mapCenter: args.mapCenter,
      });

      const resourceName = resource.vehicle
        ? resource.vehicle.title
        : (resource.technicians[0]?.displayName ?? "Unknown");

      return (
        <div key={`start-end-${index}`}>
          <MarkerF
            position={origin}
            icon={{
              url: createSVGMarker("FaHome"),
              scaledSize: new google.maps.Size(30, 30),
            }}
            title={`Start/End point for ${resourceName}`}
            onClick={() => args.setSelectedMarker(null)}
          />
        </div>
      );
    });

  return { stopMarkers, startEndMarkers };
}

function createSVGMarker(icon: "FaHome" | "FaFlag"): string {
  // This creates a 5-pointed star path
  const starPath = `M20 4
        L24.5 13.5
        L35 15
        L27.5 22.5
        L29.5 33
        L20 28
        L10.5 33
        L12.5 22.5
        L5 15
        L15.5 13.5
        Z`;

  const faHomeD =
    "M280.37 148.26L96 300.11V464a16 16 0 0 0 16 16l112.06-.29a16 16 0 0 0 15.92-16V368a16 16 0 0 1 16-16h64a16 16 0 0 1 16 16v95.64a16 16 0 0 0 16 16.05L464 480a16 16 0 0 0 16-16V300L295.67 148.26a12.19 12.19 0 0 0-15.3 0zM571.6 251.47L488 182.56V44.05a12 12 0 0 0-12-12h-56a12 12 0 0 0-12 12v72.61L318.47 43a48 48 0 0 0-61 0L4.34 251.47a12 12 0 0 0-1.6 16.9l25.5 31A12 12 0 0 0 45.15 301l235.22-193.74a12.19 12.19 0 0 1 15.3 0L530.9 301a12 12 0 0 0 16.9-1.6l25.5-31a12 12 0 0 0-1.7-16.93z";
  const faFlagD =
    "M349.565 98.783C295.978 98.783 251.721 64 184.348 64c-24.955 0-47.309 4.384-68.045 12.013a55.947 55.947 0 0 0 3.586-23.562C118.117 24.015 94.806 1.206 66.338.048 34.345-1.254 8 24.296 8 56c0 19.026 9.497 35.825 24 45.945V488c0 13.255 10.745 24 24 24h16c13.255 0 24-10.745 24-24v-94.4c28.311-12.064 63.582-22.122 114.435-22.122 53.588 0 97.844 34.783 165.217 34.783 48.169 0 86.667-16.294 122.505-40.858C506.84 359.452 512 349.571 512 339.045v-243.1c0-23.393-24.269-38.87-45.485-29.016-34.338 15.948-76.454 31.854-116.95 31.854z";
  const iconMap = { FaHome: faHomeD, FaFlag: faFlagD };

  const svg = `
        <svg width="30" height="30" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="${starPath}" fill="#9F7AEA" stroke="#6B46C1" stroke-width="2"/>
          <path d="${iconMap[icon]}" fill="white" transform="translate(12, 12) scale(1.25)"/>
        </svg>
      `;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

/** Get tasks in order based on VROOM route steps */
function getOrderedTasks(resource: ResourceTechnicianVehicle): ExistingTask[] {
  if (!resource.vroomRoute?.steps) return resource.taskList;

  const orderedTasks: ExistingTask[] = [];
  for (const step of resource.vroomRoute.steps) {
    if (step.type !== "job") continue;
    const task = resource.taskList.find((t) => t.id === step.description);
    if (task) orderedTasks.push(task);
  }

  // Add any remaining tasks that weren't in the route
  for (const task of resource.taskList) {
    if (!orderedTasks.find((t) => t.id === task.id)) orderedTasks.push(task);
  }

  return orderedTasks;
}
